/*
Rudradhan human-click tracker v3
================================
Purpose:
- Record only genuine browser user activations.
- Neutralize older Shopify click handlers that append js_click=1 and navigate through GET /asset-click.
- Send analytics via POST /asset-click.
- Navigate directly to the real target asset.

IMPORTANT: Load this script ONCE, as early as possible in <head>.
*/
(function () {
  "use strict";

  var ALLOWED_TYPES = [
    "gold_card",
    "price_breakup",
    "instagram_follow",
    "india_ready_status",
    "video_call_booking",
    "store_visit_booking"
  ];

  function normalizeTarget(target) {
    target = (target || "").trim();
    if (target.indexOf("//") === 0) return "https:" + target;
    return target;
  }

  function parseTrackingUrl(href) {
    var url;
    try {
      url = new URL(href, window.location.href);
    } catch (_) {
      return null;
    }

    if (url.pathname !== "/asset-click") return null;

    var assetType = (url.searchParams.get("asset_type") || "").trim();
    if (ALLOWED_TYPES.indexOf(assetType) === -1) return null;

    var target = normalizeTarget(url.searchParams.get("target") || "");
    if (!target) return null;

    try {
      var targetUrl = new URL(target, window.location.href);
      if (targetUrl.protocol !== "https:") return null;
      target = targetUrl.href;
    } catch (_) {
      return null;
    }

    return {
      endpoint: url.origin + url.pathname,
      asset_type: assetType,
      sku: (url.searchParams.get("sku") || "").trim(),
      product_id: (url.searchParams.get("product_id") || "").trim(),
      handle: (url.searchParams.get("handle") || "").trim(),
      site: (url.searchParams.get("site") || (window.location.hostname.endsWith(".us") ? "us" : "india")).trim(),
      target: target
    };
  }

  function storeInfo(anchor, info) {
    if (!anchor || !anchor.dataset || !info) return;
    anchor.dataset.rdHumanClickReady = "1";
    anchor.dataset.rdEndpoint = info.endpoint;
    anchor.dataset.rdAssetType = info.asset_type;
    anchor.dataset.rdSku = info.sku;
    anchor.dataset.rdProductId = info.product_id;
    anchor.dataset.rdHandle = info.handle;
    anchor.dataset.rdSite = info.site;
    anchor.dataset.rdTarget = info.target;
  }

  function infoFromDataset(anchor) {
    if (!anchor || !anchor.dataset || anchor.dataset.rdHumanClickReady !== "1") return null;
    return {
      endpoint: anchor.dataset.rdEndpoint || "",
      asset_type: anchor.dataset.rdAssetType || "",
      sku: anchor.dataset.rdSku || "",
      product_id: anchor.dataset.rdProductId || "",
      handle: anchor.dataset.rdHandle || "",
      site: anchor.dataset.rdSite || "india",
      target: anchor.dataset.rdTarget || ""
    };
  }

  function prepareAnchor(anchor) {
    if (!anchor || anchor.tagName !== "A") return null;

    var existing = infoFromDataset(anchor);
    if (existing) return existing;

    var info = parseTrackingUrl(anchor.getAttribute("href") || anchor.href || "");
    if (!info) return null;

    storeInfo(anchor, info);

    // Rewrite the visible/crawlable href to the actual destination.
    // Old scripts may later change it back; the window-level capture handler below still wins.
    anchor.setAttribute("href", info.target);
    return info;
  }

  function prepareTree(root) {
    if (!root) return;

    if (root.nodeType === 1 && root.tagName === "A") {
      prepareAnchor(root);
    }

    if (root.querySelectorAll) {
      var anchors = root.querySelectorAll('a[href*="/asset-click"]');
      for (var i = 0; i < anchors.length; i++) prepareAnchor(anchors[i]);
    }
  }

  function sendHumanClick(info) {
    if (!info || !info.endpoint) return;

    var body = new URLSearchParams();
    body.set("asset_type", info.asset_type);
    body.set("sku", info.sku);
    body.set("product_id", info.product_id);
    body.set("handle", info.handle);
    body.set("site", info.site);
    body.set("target", info.target);
    body.set("interaction", "trusted_click");

    if (navigator.sendBeacon) {
      try {
        if (navigator.sendBeacon(info.endpoint, body)) return;
      } catch (_) {}
    }

    if (window.fetch) {
      try {
        fetch(info.endpoint, {
          method: "POST",
          body: body,
          keepalive: true,
          credentials: "omit",
          mode: "cors"
        }).catch(function () {});
      } catch (_) {}
    }
  }

  function findAnchor(event) {
    var node = event.target;
    if (!node || !node.closest) return null;
    return node.closest("a");
  }

  function getTrackingInfo(anchor) {
    var info = infoFromDataset(anchor);
    if (info) return info;

    // Handles a legacy script that has already appended js_click=1/t=... to the URL.
    info = parseTrackingUrl(anchor.getAttribute("href") || anchor.href || "");
    if (info) storeInfo(anchor, info);
    return info;
  }

  function navigateDirect(anchor, info, event) {
    var openNew =
      (anchor && anchor.target === "_blank") ||
      event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1;

    if (openNew) {
      var w = window.open(info.target, "_blank", "noopener");
      if (w) w.opener = null;
    } else {
      window.location.assign(info.target);
    }
  }

  function captureHumanActivation(event) {
    if (!event.isTrusted) return;

    // click = primary/keyboard activation; auxclick = middle mouse.
    if (event.type === "click" && typeof event.button === "number" && event.button !== 0) return;
    if (event.type === "auxclick" && event.button !== 1) return;

    var anchor = findAnchor(event);
    if (!anchor) return;

    var info = getTrackingInfo(anchor);
    if (!info) return;

    // This is the key V3 behavior: prevent older document/element click handlers
    // from turning the human click into GET /asset-click?js_click=1.
    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();

    sendHumanClick(info);
    navigateDirect(anchor, info, event);
  }

  // Register on WINDOW CAPTURE immediately. Window is earlier in the event path than document/anchor.
  window.addEventListener("click", captureHumanActivation, true);
  window.addEventListener("auxclick", captureHumanActivation, true);

  function startDomPreparation() {
    prepareTree(document);

    if (!window.MutationObserver || !document.documentElement) return;

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];

        if (m.type === "attributes" && m.target && m.target.tagName === "A") {
          var href = m.target.getAttribute("href") || "";
          if (href.indexOf("/asset-click") !== -1) {
            var info = parseTrackingUrl(href);
            if (info) {
              storeInfo(m.target, info);
              m.target.setAttribute("href", info.target);
            }
          }
        }

        if (m.addedNodes) {
          for (var j = 0; j < m.addedNodes.length; j++) prepareTree(m.addedNodes[j]);
        }
      }
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["href"]
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", startDomPreparation, { once: true });
  } else {
    startDomPreparation();
  }
})();